import { useEffect, useState } from 'react'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { inputClass, selectClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'

// Contas bancarias e integracoes Sicredi/PagBank (migration 020). Segredos e certificado vao
// para a API e nunca voltam (so "•••• 1234"). Campos com data-sem-maiusculo: o maiusculo
// automatico estragaria client_id, chave PIX, URL e segredos.

type Provedor = 'sicredi' | 'pagbank'
type Servico = 'pix' | 'boleto'

interface Integracao {
  idintegracao: number
  provedor: Provedor
  servico: Servico
  ambiente: 'homologacao' | 'producao'
  client_id: string | null
  segredos: Record<string, string | null>
  certificado: { tipo: 'pfx' | 'pem'; validade: string | null } | null
  parametros: Record<string, string>
  url_padrao: string
  ultimo_teste_em: string | null
  ultimo_teste_ok: boolean | null
  ultimo_teste_msg: string | null
}

interface ContaBancaria {
  idconta_bancaria: number
  banco_codigo: string | null
  banco_nome: string
  agencia: string | null
  conta: string | null
  conta_digito: string | null
  tipo: 'corrente' | 'poupanca' | 'pagamento'
  titular: string
  titular_documento: string | null
  pix_tipo: string | null
  pix_chave: string | null
  pix_cidade: string | null
  padrao: boolean
  ativo: boolean
  observacao: string | null
  integracoes: Integracao[]
}

const NOME_INT: Record<string, string> = {
  'sicredi:pix': 'Sicredi — API PIX', 'sicredi:boleto': 'Sicredi — API Cobranca (boleto)',
  'pagbank:pix': 'PagBank — PIX', 'pagbank:boleto': 'PagBank — Boleto',
}
const CAMPOS_SEGREDO: Record<string, Array<[string, string]>> = {
  'sicredi:pix': [['client_secret', 'Client secret']],
  'sicredi:boleto': [['api_key', 'API key (x-api-key)'], ['codigo_acesso', 'Codigo de acesso (gerado no internet banking)']],
  'pagbank:pix': [['token', 'Token']],
  'pagbank:boleto': [['token', 'Token']],
}
const CAMPOS_PARAM: Record<string, Array<[string, string]>> = {
  'sicredi:pix': [],
  'sicredi:boleto': [['cooperativa', 'Cooperativa'], ['posto', 'Posto'], ['codigo_beneficiario', 'Codigo do beneficiario']],
  'pagbank:pix': [],
  'pagbank:boleto': [],
}
const BANCOS_SUGERIDOS = [['748', 'SICREDI'], ['290', 'PAGBANK (PAGSEGURO)'], ['001', 'BANCO DO BRASIL'], ['104', 'CAIXA'], ['077', 'INTER'], ['341', 'ITAU'], ['237', 'BRADESCO'], ['033', 'SANTANDER']]

const erroApi = (err: any, padrao: string) => err?.response?.data?.message || err?.response?.data?.error || padrao
const contaVazia = { banco_codigo: '748', banco_nome: 'SICREDI', agencia: '', conta: '', conta_digito: '', tipo: 'corrente', titular: '', titular_documento: '', pix_tipo: '', pix_chave: '', pix_cidade: '', padrao: false, ativo: true, observacao: '' }

function lerArquivo(f: File, como: 'base64' | 'texto'): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(new Error('Nao foi possivel ler o arquivo'))
    if (como === 'base64') r.readAsDataURL(f); else r.readAsText(f)
  })
}

export function Bancos({ onAlert }: { onAlert: (a: { type: 'success' | 'error'; message: string }) => void }) {
  const [contas, setContas] = useState<ContaBancaria[]>([])
  const [webhookPagbank, setWebhookPagbank] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [editConta, setEditConta] = useState<ContaBancaria | null>(null)
  const [formConta, setFormConta] = useState<any>(null)
  // integracao em edicao
  const [intConta, setIntConta] = useState<ContaBancaria | null>(null)
  const [intChave, setIntChave] = useState('sicredi:pix')
  const [intForm, setIntForm] = useState<any>(null)
  const [testando, setTestando] = useState<number | null>(null)

  async function carregar() {
    setCarregando(true)
    try {
      const { data } = await api.get<{ data: ContaBancaria[]; webhook_pagbank: string }>('/financeiro/bancos')
      setContas(data.data)
      setWebhookPagbank(data.webhook_pagbank)
    } catch (err) { onAlert({ type: 'error', message: erroApi(err, 'Erro ao carregar as contas bancarias') }) }
    finally { setCarregando(false) }
  }
  useEffect(() => { carregar() }, [])

  function abrirConta(c: ContaBancaria | null) {
    setEditConta(c)
    setFormConta(c ? {
      banco_codigo: c.banco_codigo ?? '', banco_nome: c.banco_nome, agencia: c.agencia ?? '', conta: c.conta ?? '', conta_digito: c.conta_digito ?? '',
      tipo: c.tipo, titular: c.titular, titular_documento: c.titular_documento ?? '', pix_tipo: c.pix_tipo ?? '', pix_chave: c.pix_chave ?? '',
      pix_cidade: c.pix_cidade ?? '', padrao: c.padrao, ativo: c.ativo, observacao: c.observacao ?? '',
    } : { ...contaVazia, padrao: contas.length === 0 })
  }

  async function salvarConta() {
    setSalvando(true)
    try {
      const corpo = { ...formConta, pix_tipo: formConta.pix_tipo || null, pix_chave: formConta.pix_chave || null }
      if (editConta) await api.put(`/financeiro/bancos/${editConta.idconta_bancaria}`, corpo)
      else await api.post('/financeiro/bancos', corpo)
      onAlert({ type: 'success', message: 'Conta bancaria salva.' })
      setFormConta(null)
      carregar()
    } catch (err) { onAlert({ type: 'error', message: erroApi(err, 'Erro ao salvar a conta bancaria') }) }
    finally { setSalvando(false) }
  }

  async function excluirConta(c: ContaBancaria) {
    if (!window.confirm(`Excluir a conta ${c.banco_nome} ${c.agencia ?? ''}/${c.conta ?? ''} e as integracoes dela?`)) return
    try { await api.delete(`/financeiro/bancos/${c.idconta_bancaria}`); carregar() }
    catch (err) { onAlert({ type: 'error', message: erroApi(err, 'Erro ao excluir') }) }
  }

  function abrirIntegracao(c: ContaBancaria, chave: string) {
    const [prov, serv] = chave.split(':')
    const atual = c.integracoes.find(i => i.provedor === prov && i.servico === serv)
    setIntConta(c)
    setIntChave(chave)
    setIntForm({
      ambiente: atual?.ambiente ?? 'homologacao', client_id: atual?.client_id ?? '', segredos: {},
      parametros: { ...(atual?.parametros ?? {}) }, atual: atual ?? null,
      cert_pfx: null as File | null, cert_senha: '', cert_crt: null as File | null, cert_key: null as File | null,
    })
  }

  async function salvarIntegracao() {
    if (!intConta) return
    setSalvando(true)
    try {
      const [prov, serv] = intChave.split(':')
      const corpo: any = { ambiente: intForm.ambiente, client_id: intForm.client_id, segredos: intForm.segredos, parametros: intForm.parametros }
      if (intForm.cert_pfx) corpo.certificado = { pfx: await lerArquivo(intForm.cert_pfx, 'base64'), senha: intForm.cert_senha }
      else if (intForm.cert_crt && intForm.cert_key) corpo.certificado = { cert: await lerArquivo(intForm.cert_crt, 'texto'), key: await lerArquivo(intForm.cert_key, 'texto') }
      else if (intForm.cert_crt || intForm.cert_key) throw new Error('Para certificado PEM envie o .crt/.cer E a chave .key')
      await api.put(`/financeiro/bancos/${intConta.idconta_bancaria}/integracoes/${prov}/${serv}`, corpo)
      onAlert({ type: 'success', message: `${NOME_INT[intChave]} salva. Use "Testar conexao".` })
      setIntForm(null)
      carregar()
    } catch (err: any) { onAlert({ type: 'error', message: erroApi(err, err?.message || 'Erro ao salvar a integracao') }) }
    finally { setSalvando(false) }
  }

  async function testar(i: Integracao) {
    setTestando(i.idintegracao)
    try {
      const { data } = await api.post<{ data: Integracao }>(`/financeiro/integracoes/${i.idintegracao}/testar`)
      onAlert({ type: data.data.ultimo_teste_ok ? 'success' : 'error', message: data.data.ultimo_teste_msg ?? '' })
      carregar()
    } catch (err) { onAlert({ type: 'error', message: erroApi(err, 'Erro ao testar') }) }
    finally { setTestando(null) }
  }

  async function removerIntegracao(i: Integracao) {
    if (!window.confirm(`Remover a integracao ${NOME_INT[`${i.provedor}:${i.servico}`]}? As credenciais guardadas sao apagadas.`)) return
    try { await api.delete(`/financeiro/integracoes/${i.idintegracao}`); carregar() }
    catch (err) { onAlert({ type: 'error', message: erroApi(err, 'Erro ao remover') }) }
  }

  const f = formConta
  const setF = (k: string, v: any) => setFormConta((prev: any) => ({ ...prev, [k]: v }))
  const precisaCert = intChave === 'sicredi:pix'

  return (
    <div className="space-y-4" data-bancos>
      <div className="flex justify-between items-center">
        <p className="text-sm text-compumaq-textoSuave">
          Contas do clube e as credenciais das APIs de cobranca (boleto) e PIX. A conta <b>padrao</b> fornece a chave PIX da inscricao publica.
        </p>
        <button onClick={() => abrirConta(null)} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro font-medium whitespace-nowrap">+ Nova conta bancaria</button>
      </div>

      {carregando ? <div className="flex justify-center py-12"><Spinner size="lg" /></div>
        : !contas.length ? <p className="text-center py-12 text-compumaq-textoSuave bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25">Nenhuma conta bancaria cadastrada.</p>
        : contas.map(c => (
          <div key={c.idconta_bancaria} data-conta-bancaria={c.idconta_bancaria} className={`bg-compumaq-pretoSuave rounded-xl border p-4 shadow-sm space-y-3 ${c.padrao ? 'border-compumaq-dourado' : 'border-compumaq-dourado/25'} ${c.ativo ? '' : 'opacity-60'}`}>
            <div className="flex flex-wrap justify-between gap-2">
              <div>
                <p className="font-semibold text-compumaq-texto">
                  {c.banco_codigo ? `${c.banco_codigo} - ` : ''}{c.banco_nome}
                  {c.padrao && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-compumaq-dourado/20 text-compumaq-doradoClaro">Padrao</span>}
                  {!c.ativo && <span className="ml-2 text-xs text-compumaq-textoSuave">(inativa)</span>}
                </p>
                <p className="text-sm text-compumaq-textoSuave">
                  Ag {c.agencia || '—'} · Conta {c.conta || '—'}{c.conta_digito ? `-${c.conta_digito}` : ''} ({c.tipo}) · {c.titular}{c.titular_documento ? ` · ${c.titular_documento}` : ''}
                </p>
                <p className="text-sm text-compumaq-textoSuave">PIX: {c.pix_chave ? `${c.pix_chave} (${c.pix_tipo})` : '—'}{c.pix_cidade ? ` · ${c.pix_cidade}` : ''}</p>
              </div>
              <div className="flex gap-3 items-start">
                <button onClick={() => abrirConta(c)} className="text-xs text-compumaq-doradoClaro font-medium">Editar</button>
                <button onClick={() => excluirConta(c)} className="text-xs text-red-500">Excluir</button>
              </div>
            </div>
            <div className="border-t border-compumaq-dourado/25 pt-2 space-y-2">
              <p className="text-xs font-semibold text-compumaq-textoSuave uppercase">Integracoes</p>
              {c.integracoes.map(i => (
                <div key={i.idintegracao} data-integracao={`${i.provedor}:${i.servico}`} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-compumaq-texto min-w-[230px]">{NOME_INT[`${i.provedor}:${i.servico}`]}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${i.ambiente === 'producao' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{i.ambiente === 'producao' ? 'producao' : 'homologacao'}</span>
                  {i.certificado && <span className="text-xs text-compumaq-textoSuave">cert. {i.certificado.tipo}{i.certificado.validade ? ` ate ${i.certificado.validade.split('-').reverse().join('/')}` : ''}</span>}
                  <span className={`text-xs flex-1 ${i.ultimo_teste_ok == null ? 'text-compumaq-textoSuave' : i.ultimo_teste_ok ? 'text-green-600' : 'text-red-600'}`}>
                    {i.ultimo_teste_ok == null ? 'nao testada' : i.ultimo_teste_msg}
                  </span>
                  <button onClick={() => testar(i)} disabled={testando === i.idintegracao} className="text-xs text-green-600 font-medium disabled:opacity-50">{testando === i.idintegracao ? 'Testando...' : 'Testar conexao'}</button>
                  <button onClick={() => abrirIntegracao(c, `${i.provedor}:${i.servico}`)} className="text-xs text-compumaq-doradoClaro font-medium">Configurar</button>
                  <button onClick={() => removerIntegracao(i)} className="text-xs text-red-500">Remover</button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                {Object.keys(NOME_INT).filter(k => !c.integracoes.some(i => `${i.provedor}:${i.servico}` === k)).map(k => (
                  <button key={k} onClick={() => abrirIntegracao(c, k)} className="text-xs px-2 py-1 border border-compumaq-dourado/40 rounded-lg hover:bg-compumaq-preto">+ {NOME_INT[k]}</button>
                ))}
              </div>
            </div>
          </div>
        ))}

      {/* conta bancaria */}
      <Modal open={!!formConta} onClose={() => setFormConta(null)} title={editConta ? 'Editar conta bancaria' : 'Nova conta bancaria'} size="lg">
        {f && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-compumaq-textoSuave mb-1">Codigo do banco</label>
                <input className={inputClass} aria-label="Codigo do banco" value={f.banco_codigo} onChange={e => {
                  const nome = BANCOS_SUGERIDOS.find(b => b[0] === e.target.value)?.[1]
                  setFormConta((p: any) => ({ ...p, banco_codigo: e.target.value, ...(nome ? { banco_nome: nome } : {}) }))
                }} />
              </div>
              <div className="sm:col-span-3">
                <label className="block text-xs text-compumaq-textoSuave mb-1">Banco *</label>
                <input className={inputClass} aria-label="Banco" list="bancos-sug" value={f.banco_nome} onChange={e => setF('banco_nome', e.target.value)} />
                <datalist id="bancos-sug">{BANCOS_SUGERIDOS.map(b => <option key={b[0]} value={b[1]} />)}</datalist>
              </div>
              <div>
                <label className="block text-xs text-compumaq-textoSuave mb-1">Agencia / cooperativa</label>
                <input className={inputClass} aria-label="Agencia" value={f.agencia} onChange={e => setF('agencia', e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-compumaq-textoSuave mb-1">Conta</label>
                <input className={inputClass} aria-label="Conta" value={f.conta} onChange={e => setF('conta', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-compumaq-textoSuave mb-1">Digito</label>
                <input className={inputClass} aria-label="Digito" value={f.conta_digito} onChange={e => setF('conta_digito', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-compumaq-textoSuave mb-1">Tipo</label>
                <select className={selectClass} value={f.tipo} onChange={e => setF('tipo', e.target.value)}>
                  <option value="corrente">Corrente</option><option value="poupanca">Poupanca</option><option value="pagamento">Pagamento</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-compumaq-textoSuave mb-1">Titular *</label>
                <input className={inputClass} aria-label="Titular" value={f.titular} onChange={e => setF('titular', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-compumaq-textoSuave mb-1">CNPJ/CPF do titular</label>
                <input className={inputClass} aria-label="Documento do titular" value={f.titular_documento} onChange={e => setF('titular_documento', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-compumaq-textoSuave mb-1">Tipo da chave PIX</label>
                <select className={selectClass} aria-label="Tipo da chave PIX" value={f.pix_tipo} onChange={e => setF('pix_tipo', e.target.value)}>
                  <option value="">—</option><option value="cnpj">CNPJ</option><option value="cpf">CPF</option><option value="email">E-mail</option><option value="telefone">Telefone</option><option value="aleatoria">Aleatoria</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-compumaq-textoSuave mb-1">Chave PIX</label>
                <input className={inputClass} aria-label="Chave PIX" data-sem-maiusculo value={f.pix_chave} onChange={e => setF('pix_chave', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-compumaq-textoSuave mb-1">Cidade (no QR PIX)</label>
                <input className={inputClass} aria-label="Cidade do PIX" value={f.pix_cidade} onChange={e => setF('pix_cidade', e.target.value)} />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" aria-label="Conta padrao" checked={f.padrao} onChange={e => setF('padrao', e.target.checked)} /> Conta padrao (chave PIX da inscricao publica)</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={f.ativo} onChange={e => setF('ativo', e.target.checked)} /> Ativa</label>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-compumaq-dourado/25">
              <button onClick={() => setFormConta(null)} className="px-4 py-2 text-sm border border-compumaq-dourado/25 rounded-lg">Fechar</button>
              <button onClick={salvarConta} disabled={salvando} className="px-4 py-2 text-sm bg-compumaq-dourado text-compumaq-texto rounded-lg font-medium disabled:opacity-50">{salvando ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        )}
      </Modal>

      {/* integracao */}
      <Modal open={!!intForm} onClose={() => setIntForm(null)} title={`${NOME_INT[intChave]} — ${intConta?.banco_nome ?? ''}`} size="lg">
        {intForm && (
          <div className="space-y-3" data-sem-maiusculo>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-compumaq-textoSuave mb-1">Ambiente</label>
                <select className={selectClass} aria-label="Ambiente" value={intForm.ambiente} onChange={e => setIntForm({ ...intForm, ambiente: e.target.value })}>
                  <option value="homologacao">Homologacao (testes)</option><option value="producao">Producao</option>
                </select>
              </div>
              {intChave === 'sicredi:pix' && (
                <div>
                  <label className="block text-xs text-compumaq-textoSuave mb-1">Client ID</label>
                  <input className={inputClass} aria-label="Client ID" value={intForm.client_id} onChange={e => setIntForm({ ...intForm, client_id: e.target.value })} />
                </div>
              )}
              {CAMPOS_SEGREDO[intChave].map(([k, rot]) => (
                <div key={k}>
                  <label className="block text-xs text-compumaq-textoSuave mb-1">{rot}</label>
                  <input type="password" autoComplete="new-password" className={inputClass} aria-label={rot}
                    placeholder={intForm.atual?.segredos?.[k] ? `${intForm.atual.segredos[k]} (deixe em branco para manter)` : ''}
                    value={intForm.segredos[k] ?? ''} onChange={e => setIntForm({ ...intForm, segredos: { ...intForm.segredos, [k]: e.target.value } })} />
                </div>
              ))}
              {CAMPOS_PARAM[intChave].map(([k, rot]) => (
                <div key={k}>
                  <label className="block text-xs text-compumaq-textoSuave mb-1">{rot}</label>
                  <input className={inputClass} aria-label={rot} value={intForm.parametros[k] ?? ''} onChange={e => setIntForm({ ...intForm, parametros: { ...intForm.parametros, [k]: e.target.value } })} />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-xs text-compumaq-textoSuave mb-1">URL da API (so altere se o banco mudar o endereco)</label>
                <input className={inputClass} aria-label="URL da API" placeholder={intForm.atual?.url_padrao ?? 'padrao do provedor'} value={intForm.parametros.url_base ?? ''} onChange={e => setIntForm({ ...intForm, parametros: { ...intForm.parametros, url_base: e.target.value } })} />
              </div>
            </div>
            {(precisaCert || intChave.startsWith('sicredi')) && (
              <div className="border-t border-compumaq-dourado/25 pt-2 space-y-2">
                <p className="text-sm font-semibold text-compumaq-texto">Certificado digital {precisaCert ? '(obrigatorio no PIX Sicredi)' : '(opcional)'}</p>
                {intForm.atual?.certificado
                  ? <p className="text-xs text-green-600">Certificado {intForm.atual.certificado.tipo} guardado{intForm.atual.certificado.validade ? `, valido ate ${intForm.atual.certificado.validade.split('-').reverse().join('/')}` : ''}. Envie outro so para trocar.</p>
                  : <p className="text-xs text-amber-600">Nenhum certificado guardado.</p>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <label className="block text-xs text-compumaq-textoSuave mb-1">Arquivo .pfx / .p12</label>
                    <input type="file" aria-label="Certificado pfx" accept=".pfx,.p12" onChange={e => setIntForm({ ...intForm, cert_pfx: e.target.files?.[0] ?? null })} />
                    <input type="password" autoComplete="new-password" className={inputClass + ' mt-1'} aria-label="Senha do certificado" placeholder="Senha do .pfx" value={intForm.cert_senha} onChange={e => setIntForm({ ...intForm, cert_senha: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs text-compumaq-textoSuave mb-1">ou certificado .crt/.cer + chave .key (PEM)</label>
                    <input type="file" aria-label="Certificado crt" accept=".crt,.cer,.pem" onChange={e => setIntForm({ ...intForm, cert_crt: e.target.files?.[0] ?? null })} />
                    <input type="file" aria-label="Chave key" accept=".key,.pem" className="mt-1" onChange={e => setIntForm({ ...intForm, cert_key: e.target.files?.[0] ?? null })} />
                  </div>
                </div>
              </div>
            )}
            {intChave.startsWith('pagbank') && (
              <p className="text-xs text-compumaq-textoSuave">URL de notificacao para cadastrar no PagBank: <span className="font-mono">{webhookPagbank}</span></p>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-compumaq-dourado/25">
              <button onClick={() => setIntForm(null)} className="px-4 py-2 text-sm border border-compumaq-dourado/25 rounded-lg">Fechar</button>
              <button onClick={salvarIntegracao} disabled={salvando} className="px-4 py-2 text-sm bg-compumaq-dourado text-compumaq-texto rounded-lg font-medium disabled:opacity-50">{salvando ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
